/**
 * Universal Intake — pure ambient-capture detectors (INTAKE-PRD §2.8.3, slice
 * U-E). Two conservative, React-free predicates the dock uses to OFFER intake
 * (never to auto-consume): `looksTabular` for a pasted table, `looksLikeUrl` for
 * a bare page URL. Kept pure/testable here (no DOM, no fetch) so the entry gate
 * covers the decision without a browser; the dock imports them from `@excellent/ui`.
 *
 * Both are deliberately STRICT: a false positive turns a normal paste into an
 * "Import as data" offer, so ambiguity resolves to `false` and the user keeps the
 * plain paste. The offer always carries a "Paste as text" escape (§2.8.3).
 */

/** Hard cap — a paste beyond this is never inspected as a table (§2.8.3). */
export const LOOKS_TABULAR_MAX_BYTES = 1_000_000;

/** Delimiters a tabular paste may use, in preference order. */
const DELIMITERS = [",", "\t", ";", "|"] as const;

/** Minimum rows (a header + ≥2 data lines) and columns (≥2) to read as a table. */
const MIN_LINES = 3;
const MIN_COLUMNS = 2;

/**
 * Count a delimiter's occurrences OUTSIDE double-quoted spans — so an embedded
 * `","` inside a quoted CSV field doesn't inflate the column count. Pure scan.
 */
function delimiterCount(line: string, delim: string): number {
  let count = 0;
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && ch === delim) count++;
  }
  return count;
}

/** True when a JSON array-of-objects (`[{…}]`) — the pasted-JSON tabular shape. */
function looksLikeJsonArray(text: string): boolean {
  const t = text.trim();
  if (!t.startsWith("[")) return false;
  try {
    const parsed = JSON.parse(t);
    return Array.isArray(parsed) && parsed.length > 0 && parsed.every((r) => r != null && typeof r === "object" && !Array.isArray(r));
  } catch {
    return false;
  }
}

/** True when NDJSON — ≥MIN_LINES non-blank lines each a JSON object. */
function looksLikeNdjson(lines: string[]): boolean {
  if (lines.length < MIN_LINES) return false;
  for (const line of lines) {
    const t = line.trim();
    if (!t.startsWith("{") || !t.endsWith("}")) return false;
    try {
      const parsed = JSON.parse(t);
      if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) return false;
    } catch {
      return false;
    }
  }
  return true;
}

/**
 * Conservative: does this pasted text look like a table we should OFFER to
 * import? True only when one holds (§2.8.3):
 *   • a delimited table: ≥3 non-blank lines that share a consistent, ≥1
 *     occurrence count of one delimiter (≥2 columns), OR
 *   • a JSON array-of-objects (`[{…}]`), OR
 *   • NDJSON (every line a JSON object).
 * A single-line paste, prose, a 2-line ambiguity, or anything over the size cap
 * is NEVER intercepted.
 */
export function looksTabular(text: string): boolean {
  if (!text || text.length > LOOKS_TABULAR_MAX_BYTES) return false;

  // JSON tabular shapes short-circuit (they aren't line-delimited tables).
  if (looksLikeJsonArray(text)) return true;

  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < MIN_LINES) return false; // single-line + 2-line never intercept
  if (looksLikeNdjson(lines)) return true;

  // A delimited table: some delimiter must appear a CONSISTENT, ≥MIN_COLUMNS-1
  // times on EVERY line (ragged column counts ⇒ not a clean table ⇒ don't offer).
  for (const delim of DELIMITERS) {
    const first = delimiterCount(lines[0]!, delim);
    if (first < MIN_COLUMNS - 1) continue;
    if (lines.every((l) => delimiterCount(l, delim) === first)) return true;
  }
  return false;
}

/** A rough "N rows × M columns" for the dock offer row — best-effort, for copy
 *  only (uses the first winning delimiter / JSON length). Never load-bearing. */
export function tabularShape(text: string): { rows: number; columns: number } | null {
  if (looksLikeJsonArray(text)) {
    try {
      const parsed = JSON.parse(text.trim()) as Array<Record<string, unknown>>;
      const columns = parsed.length ? Object.keys(parsed[0] ?? {}).length : 0;
      return { rows: parsed.length, columns };
    } catch {
      return null;
    }
  }
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < MIN_LINES) return null;
  if (looksLikeNdjson(lines)) {
    try {
      const columns = Object.keys(JSON.parse(lines[0]!.trim()) as Record<string, unknown>).length;
      return { rows: lines.length, columns };
    } catch {
      return null;
    }
  }
  for (const delim of DELIMITERS) {
    const first = delimiterCount(lines[0]!, delim);
    if (first < MIN_COLUMNS - 1) continue;
    if (lines.every((l) => delimiterCount(l, delim) === first)) {
      // header row counts as columns; data rows exclude it.
      return { rows: Math.max(0, lines.length - 1), columns: first + 1 };
    }
  }
  return null;
}

/**
 * True when the trimmed input is a bare http(s) URL — the dock's "Summarize into
 * workspace" branch (§2.8.3). Conservative: a single token, http/https scheme, a
 * parseable URL with a host. Prose that merely CONTAINS a link is not a bare URL.
 */
export function looksLikeUrl(text: string): boolean {
  const t = (text ?? "").trim();
  if (!t || /\s/.test(t)) return false; // a bare URL has no whitespace
  if (!/^https?:\/\//i.test(t)) return false;
  try {
    const u = new URL(t);
    return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.length > 0;
  } catch {
    return false;
  }
}
